import { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import colors from "../../../../theme/Colours";
import {
  SalesAnalyticsCardLabel,
  SalesAnalyticsContentBox,
  SalesAnalyticsOverflowBox,
  StyledSalesAnalyticsCard,
} from "../styles";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

type TrendPoint = {
  freq: string;
  vol: string | number;
  weekStartDate?: string;
  weekEndDate?: string;
};

type PeriodData = {
  period: "current" | "one" | "two" | string;
  name: string;
  frequency?: string;
  values: TrendPoint[];
};

export default function SalesVolumeTrend({
  comparisonFilterEnabled,
  data,
  periodValues,
}: {
  comparisonFilterEnabled?: boolean;
  data: {
    key: string;
    name: string;
    unit: string;
    details: PeriodData[];
  };
  periodValues: any;
}) {
  // Prepare period data maps for quick lookup
  const periodData = useMemo(() => {
    const details = data?.details || [];
    const period1 = details.find((d) => d.period === "one")?.values || [];
    const period2 = details.find((d) => d.period === "two")?.values || [];
    const current = details.find((d) => d.period === "current")?.values || [];

    const createMap = (arr: TrendPoint[]) => ({
      vol: new Map(arr.map((p) => [p.freq, Number(p.vol) || 0])),
      freqs: arr.map((p) => p.freq),
      weekDates: new Map(
        arr.map((p) => [
          p.freq,
          {
            startDate: p.weekStartDate,
            endDate: p.weekEndDate,
          },
        ])
      ),
    });

    return {
      period1: createMap(period1),
      period2: createMap(period2),
      current: createMap(current),
    };
  }, [data]);

  // Determine labels based on filter mode
  const labels = useMemo(() => {
    if (!comparisonFilterEnabled) {
      // Create labels with dates for current period
      return periodData.current.freqs.map((freq) => {
        const weekDates = periodData.current.weekDates.get(freq);
        if (weekDates?.startDate && weekDates?.endDate) {
          const formatDate = (dateStr: string) => {
            const date = new Date(dateStr);
            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const year = String(date.getFullYear()).slice(-2);
            return `${day}/${month}/${year}`;
          };
          return `${freq}\n(${formatDate(weekDates.startDate)} - ${formatDate(weekDates.endDate)})`;
        }
        return freq;
      });
    }

    // When compare periods is enabled, use Period 1 and Period 2 with date ranges
    return [
      periodValues?.["Period 1"] || "Period 1",
      periodValues?.["Period 2"] || "Period 2",
    ];
  }, [comparisonFilterEnabled, periodData, periodValues]);

  // Build chart datasets
  const chartData = useMemo(() => {
    const commonBarStyle = {};

    if (comparisonFilterEnabled) {
      const { period1, period2 } = periodData;
      // When compare periods is enabled, we show aggregated data for Period 1 and Period 2
      const period1Total = Array.from(period1.vol.values()).reduce(
        (sum, val) => sum + val,
        0
      );
      const period2Total = Array.from(period2.vol.values()).reduce(
        (sum, val) => sum + val,
        0
      );

      return {
        labels,
        datasets: [
          {
            label: "Sales Volume",
            data: [period1Total, period2Total],
            backgroundColor: [colors.linePlotBlue, colors.linePlotOrange],
            borderColor: [colors.linePlotBlue, colors.linePlotOrange],
            borderWidth: 1,
            ...commonBarStyle,
          },
        ],
      };
    }

    return {
      labels,
      datasets: [
        {
          label: "Current sales volume",
          data: periodData.current.freqs.map(
            (f) => periodData.current.vol.get(f) ?? 0
          ),
          borderColor: colors.linePlotBlue,
          backgroundColor: colors.linePlotBlue,
          ...commonBarStyle,
        },
      ],
    };
  }, [comparisonFilterEnabled, labels, periodData]);

  // Chart options with tooltip and legend customization
  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: colors.background2,
          titleColor: colors.salesAnalyticsCardLabelText,
          bodyColor: colors.salesAnalyticsCardLabelText,
          borderColor: colors.greyBorder,
          borderWidth: 1,
          padding: 12,
          displayColors: true,
          callbacks: {
            title: () => "",
            label: (context: any) => {
              const value = context.parsed.y;
              return value !== undefined ? `${value}L` : "";
            },
          },
        },
      },
      scales: {
        x: {
          grid: { color: colors.greyBorder, lineWidth: 1, drawBorder: false },
          border: { color: colors.greyBorder },
          ticks: {
            color: colors.salesAnalyticsCardLabelText,
            font: { size: 9 },
            maxRotation: 0,
            minRotation: 0,
            autoSkip: false,
            callback: function (value: any) {
              const label = labels[value];
              if (!label) return value;

              if (comparisonFilterEnabled) {
                // Center align period labels when compare periods is enabled
                const lines = label.split("\n");
                return lines.length > 1 ? lines : label;
              }

              return label.split("\n");
            },
          },
          // Center align labels when compare periods is enabled
          ...(comparisonFilterEnabled && {
            afterBuildTicks: (scale: any) => {
              if (scale.ticks.length === 2) {
                // Ensure proper positioning for two bars
                scale.ticks[0].label = labels[0];
                scale.ticks[1].label = labels[1];
              }
              return scale.ticks;
            },
          }),
        },
        y: {
          beginAtZero: true,
          grid: { color: colors.greyBorder, lineWidth: 1, drawBorder: false },
          border: { color: colors.greyBorder },
          ticks: {
            color: colors.salesAnalyticsCardLabelText,
            font: { size: 11 },
            callback: (value: any) => {
              const numValue = Number(value);
              if (numValue >= 100000) return `${numValue / 100000}L`;
              if (numValue >= 1000) return `${numValue / 1000}k`;
              return numValue.toString();
            },
          },
          title: {
            display: true,
            text: "Volume (Liters)",
            color: colors.salesAnalyticsCardHelperText,
            font: { size: 12 },
          },
        },
      },
      interaction: { intersect: true, mode: "nearest" as const },
    }),
    [comparisonFilterEnabled, periodValues]
  );

  return (
    <StyledSalesAnalyticsCard>
      <SalesAnalyticsCardLabel>{data?.name}</SalesAnalyticsCardLabel>
      <SalesAnalyticsContentBox>
        <SalesAnalyticsOverflowBox>
          <Bar data={chartData} options={options} />
        </SalesAnalyticsOverflowBox>
      </SalesAnalyticsContentBox>
    </StyledSalesAnalyticsCard>
  );
}
